import { Injectable } from '@angular/core';
import { Router, UrlTree, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { RootRoutes } from '@root/root-routes';
import { AppState } from '@root/app-state.module';
import { selectIsLoggedIn } from '@store/user/user.selector';
import { BrowserStorageService } from '@shared/services/browser-storage.service';
import { BrowserStorageKeys } from '@shared/models/browser-storage.model';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {
  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly browserStorageService: BrowserStorageService
  ) {}

  canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.store
      .pipe(
        select(selectIsLoggedIn),
        map((isLoggedIn: boolean) => {
          let route = RootRoutes.Login;

          const isValidReturnUrl = Boolean(state.url && this.isValidReturnUrl(state.url));
          const isRedirectReturnUrl = Boolean(state.url && this.isRedirectReturnUrl(state.url));
          const isRestoredReturnUrl = Boolean(this.router.getCurrentNavigation()?.extras?.state?.restoredReturnUrl);

          if (!isLoggedIn && isRedirectReturnUrl && isRestoredReturnUrl) {
            return true;
          }

          if (!isLoggedIn && state.url
            && !state.url.startsWith(`/${RootRoutes.Login}`)
            && !state.url.startsWith(`/${RootRoutes.Refresh}`)
            && isValidReturnUrl) {
            // Persist the intended destination so the post-login effect can return here.
            this.browserStorageService.set(BrowserStorageKeys.RETURN_URL, state.url);
          }

          // Restore an existing frontend session before continuing.
          if (!isLoggedIn && this.browserStorageService.get(BrowserStorageKeys.LOGGED_IN)) {
            route = RootRoutes.Refresh;
          }

          return isLoggedIn || this.router.createUrlTree([route]);
        }));
  }

  private isValidReturnUrl(url: string): boolean {
    return /^\/(unlock|dashboard|search|saved-properties|orders|settings|reports)(\/.*)?$/.test(url)
      || this.isRedirectReturnUrl(url);
  }

  private isRedirectReturnUrl(url: string): boolean {
    return /^\/redirect\/[^/?#]+\/[^/?#]+\/[^/?#]+([?#].*)?$/.test(url);
  }
}
